export interface ExpertiseStage {
  id: number;
  label: string;
  description: string;
  color: string;
  icon: string;
  percentage: number;
}

export const EXPERTISE_STAGES: ExpertiseStage[] = [
  { id: 10, label: 'Bureau à encoder', description: 'Dossier tout juste créé', color: '#ff6b6b', icon: '📋', percentage: 12.5 },
  { id: 20, label: 'Bureau à planifier', description: 'Préparation / prise de rendez-vous', color: '#ff8e53', icon: '🔍', percentage: 25 },
  { id: 30, label: 'RV en attente', description: 'RV en attente de planification', color: '#f39c12', icon: '📊', percentage: 37.5 },
  { id: 40, label: 'Expert à visiter', description: 'Visite terrain à organiser', color: '#e67e22', icon: '✅', percentage: 50 },
  { id: 50, label: 'Expert à achever', description: 'Rapport d\'expertise à boucler', color: '#f1c40f', icon: '📝', percentage: 62.5 },
  { id: 60, label: 'Bureau à valoriser', description: 'Rapport d\'expertise à valoriser', color: '#52c41a', icon: '🏁', percentage: 62.5 },
  { id: 70, label: 'Bureau à achever', description: 'Rapport d\'expertise à achever', color: '#1890ff', icon: '💼', percentage: 62.5 },
  { id: 75, label: 'FCH à achever', description: 'Fiche à achever', color: '#1890ff', icon: '📄', percentage: 62.5 },
  { id: 80, label: 'Attente doc/infos', description: 'Attentes administratives', color: '#722ed1', icon: '⚡', percentage: 75 },
  { id: 90, label: 'Attente paiement', description: 'Attentes paiements, relances', color: '#eb2f96', icon: '📧', percentage: 75 },
  { id: 100, label: 'Attente confirmation', description: 'Validations externes', color: '#13c2c2', icon: '✔️', percentage: 87.5 },
  { id: 110, label: 'À transmettre', description: 'Prêt à être envoyé', color: '#52c41a', icon: '📤', percentage: 87.5 },
  { id: 120, label: 'Terminé', description: 'Expertise finalisée', color: '#10B981', icon: '✅', percentage: 100 },
  { id: 130, label: 'Archives', description: 'Dossier archivé', color: '#6B7280', icon: '📁', percentage: 100 }
];

export const STATUS_MAPPING: Record<string, ExpertiseStage> = {
  'bureau à encoder': EXPERTISE_STAGES[0],
  'bureau a encoder': EXPERTISE_STAGES[0],
  
  'bureau à planifier': EXPERTISE_STAGES[1],
  'bureau a planifier': EXPERTISE_STAGES[1],
  
  'rv en attente': EXPERTISE_STAGES[2],
  
  'expert à visiter': EXPERTISE_STAGES[3],
  'expert a visiter': EXPERTISE_STAGES[3],
  
  'expert à achever': EXPERTISE_STAGES[4],
  'expert a achever': EXPERTISE_STAGES[4],
  
  'bureau à valoriser': EXPERTISE_STAGES[5],
  'bureau a valoriser': EXPERTISE_STAGES[5],
  
  'bureau à achever': EXPERTISE_STAGES[6],
  'bureau a achever': EXPERTISE_STAGES[6],
  'fch à achever': EXPERTISE_STAGES[7],
  'fch a achever': EXPERTISE_STAGES[7],
  
  'attente doc/infos': EXPERTISE_STAGES[8],
  'attente documents/infos': EXPERTISE_STAGES[8],
  
  'attente paiement': EXPERTISE_STAGES[9],
  'attente de paiement + conf': EXPERTISE_STAGES[9],
  'demande annulee + att paiement client': EXPERTISE_STAGES[9],
  'attente de facturation': EXPERTISE_STAGES[9],
  'rappel paiement client': EXPERTISE_STAGES[9],
  '1er rappel paiement courtier': EXPERTISE_STAGES[9],
  '2eme rappel paiement courtier': EXPERTISE_STAGES[9],
  '3eme rappel paiement courtier sms': EXPERTISE_STAGES[9],
  '3eme rappel tf + mail (menace solvency)': EXPERTISE_STAGES[9],
  'rappel paiement courtier': EXPERTISE_STAGES[9],
  'attente de facturation mensuelle': EXPERTISE_STAGES[9],
  'solvency': EXPERTISE_STAGES[9],
  
  'attente confirmation': EXPERTISE_STAGES[10],
  'facture en attente confirmation': EXPERTISE_STAGES[10],
  'mandat de vente en cours': EXPERTISE_STAGES[10],
  
  'à transmettre': EXPERTISE_STAGES[11],
  'a transmettre': EXPERTISE_STAGES[11],
  
  'terminé': EXPERTISE_STAGES[12],
  'termine': EXPERTISE_STAGES[12],
  'finalisé': EXPERTISE_STAGES[12],
  'finalise': EXPERTISE_STAGES[12],
  
  'archives': EXPERTISE_STAGES[13],
  'archivé': EXPERTISE_STAGES[13],
  'archive': EXPERTISE_STAGES[13],
  'archivée': EXPERTISE_STAGES[13],
  
  // Common edge cases - all treated as Terminé
  'sans suite': EXPERTISE_STAGES[12],
  'stand-by': EXPERTISE_STAGES[12],
  'standby': EXPERTISE_STAGES[12],
  'annulé': EXPERTISE_STAGES[12],
  'annule': EXPERTISE_STAGES[12],
  'clôturé': EXPERTISE_STAGES[12],
  'cloture': EXPERTISE_STAGES[12],
  'fermé': EXPERTISE_STAGES[12],
  'ferme': EXPERTISE_STAGES[12]
};

export const getStageFromStatus = (statusAffaire: string): ExpertiseStage | null => {
  if (!statusAffaire) return null;
  
  const normalizedStatus = statusAffaire.toLowerCase().trim();
  
  for (const [pattern, stage] of Object.entries(STATUS_MAPPING)) {
    if (pattern.includes('*')) {
      const regex = new RegExp(pattern.replace(/\*/g, '.*'), 'i');
      if (regex.test(normalizedStatus)) {
        return stage;
      }
    } else if (normalizedStatus === pattern || normalizedStatus.includes(pattern)) {
      return stage;
    }
  }
  
  // Default to "Terminé" for any unknown status
  return EXPERTISE_STAGES[12]; // Terminé stage
};

export const getCompletionPercentageFromStatus = (statusAffaire: string): number => {
  const stage = getStageFromStatus(statusAffaire);
  return stage?.percentage ?? 0;
};

export const getStageColor = (statusAffaire: string): string => {
  const stage = getStageFromStatus(statusAffaire);
  return stage?.color ?? '#6B7280';
};

export const getStageIcon = (statusAffaire: string): string => {
  const stage = getStageFromStatus(statusAffaire);
  return stage?.icon ?? '📌';
};

export const getStageDescription = (statusAffaire: string): string => {
  const stage = getStageFromStatus(statusAffaire);
  return stage?.description ?? 'En cours de traitement';
};

export const getTimelineStepFromStageId = (stageId: number): number => {
  if (stageId <= 10) return 1; // Demande
  if (stageId <= 20) return 2; // Analyse
  if (stageId <= 30) return 3; // Planification
  if (stageId <= 40) return 4; // Visite
  if (stageId <= 75) return 5; // Rédaction (50, 60, 70, 75)
  if (stageId <= 90) return 6; // Attente paiement/doc (80, 90)
  if (stageId <= 110) return 7; // Validation (100, 110)
  return 8; // Terminé (120+)
};

export const getTimelineStepsFromStatus = (statusAffaire: string) => {
  const stage = getStageFromStatus(statusAffaire);
  const currentStepNumber = stage ? getTimelineStepFromStageId(stage.id) : 0;
  
  const steps = [
    { id: 1, title: 'Demande', percentage: 0 },
    { id: 2, title: 'Analyse', percentage: 12.5 },
    { id: 3, title: 'Planification', percentage: 25 },
    { id: 4, title: 'Visite', percentage: 37.5 },
    { id: 5, title: 'Rédaction', percentage: 50 },
    { id: 6, title: 'Attente paiement/doc', percentage: 62.5 },
    { id: 7, title: 'Validation', percentage: 75 },
    { id: 8, title: 'Terminé', percentage: 87.5 },
  ];

  return steps.map(step => ({
    ...step,
    status: 
      step.id < currentStepNumber ? 'completed' :
      step.id === currentStepNumber ? 'current' :
      'pending'
  }));
};